// @file Helper functions for wall collections.
import { isAppUsing } from '@@/bits//flip'
import { __ } from '@@/bits/intl'
import type { CollectionKey, WallsFilter } from 'types/collections'

export const LIBRARY_ACCOUNT_FILTERS: WallsFilter[] = [
  'all',
  'made',
  'private_walls',
  ...(isAppUsing('templates') ? (['templates'] as WallsFilter[]) : []),
  'archived',
  'trashed',
]
export const USER_ACCOUNT_FILTERS: WallsFilter[] = [
  'all',
  ...(isAppUsing('templates') ? (['templates'] as WallsFilter[]) : []),
  'archived',
  'trashed',
]
export const TENANT_USER_ACCOUNT_FILTERS: WallsFilter[] = [
  'all',
  'made',
  ...(isAppUsing('templates') ? (['templates'] as WallsFilter[]) : []),
  'archived',
  'trashed',
]

export function filterDisplayName(filter: WallsFilter): string {
  switch (filter) {
    case 'recents':
    case 'combined_recents':
    case 'writableRecents':
      return __('Recents')
    case 'all':
      return __('All')
    case 'made':
      return __('Made')
    case 'shared':
    case 'combined_shared':
      return __('All')
    case 'writableShared':
      return __('Shared')
    case 'network':
    case 'writableNetwork':
      return __('Network')
    case 'liked':
    case 'writableLiked':
      return __('Liked')
    case 'archived':
      return __('Archived')
    case 'trashed':
      return __('Trashed')
    case 'favorites':
      return __('Favorites')
    case 'gallery':
      return __('Gallery')
    case 'private_walls':
      return __('Private')
    case 'templates':
      return __('Templates')
    // Fill these in when it's actually being used
    case 'portfolio':
    case 'contributed':
    default:
      return ''
  }
}

const GalleryTextToVizMap = {
  all: 'all',
  wall: 'grid',
  stream: 'stream',
  grid: 'matrix',
  shelf: 'shelf',
  map: 'map',
  canvas: 'free',
  timeline: 'timeline',
}

export const galleryTextArray = Object.keys(GalleryTextToVizMap)

export const galleryVizes = [
  { icon: 'gallery_outline', text: __('All'), viz: 'all' },
  { icon: 'wall_outline', text: __('Wall'), viz: 'grid' },
  { icon: 'stream_outline', text: __('Stream'), viz: 'stream' },
  { icon: 'grid_outline', text: __('Grid'), viz: 'matrix' },
  { icon: 'map_outline', text: __('Map'), viz: 'map' },
  { icon: 'canvas_outline', text: __('Canvas'), viz: 'free' },
  { icon: 'timeline_outline', text: __('Timeline'), viz: 'timeline_v2' },
]

export function filterIconName(filter: WallsFilter): string {
  const knownIcons = {
    all: 'gallery_outline',
    recents: 'recent_outline',
    made: 'made_outline',
    shared: 'handshake_outline',
    liked: 'heart_outline',
    archived: 'archive_outline',
    trashed: 'delete',
    gallery: 'compass',
    private_walls: 'password',
    templates: 'sub_grid',
  }
  return knownIcons[filter] || 'folder_outline'
}

export function getEmptyFilterCollectionCopy(filter: WallsFilter): [string, string] {
  switch (filter) {
    case 'recents':
    case 'combined_recents':
    case 'writableRecents':
      return [__('No recent padlet activity.'), __('This too shall pass.')]
    case 'made':
      return [__("You haven't made any padlets yet."), __('Go create some magic.')]
    case 'shared':
    case 'combined_shared':
    case 'combined_shared_user':
    case 'combined_shared_library':
    case 'writableShared':
      return [__('No shared padlets yet.'), __('Sharing is caring.')]
    case 'liked':
    case 'writableLiked':
      return [__("You haven't liked any padlets yet."), __('Go ahead, show your favourite padlets some love.')]
    case 'archived':
      return [__("You haven't archived any padlets yet."), __("There's no rush, but it's here when you need it.")]
    case 'trashed':
      return [__('Nothing in trash!'), __('Recently deleted padlets will be listed here.')]
    case 'favorites':
      return [__("You haven't bookmarked any padlets yet."), __('This is a safe space for the padlets you care about.')]
    case 'gallery':
      return ["Uh oh. You shouldn't be seeing this. The gallery is empty.", '']
    case 'all':
      return [__('No padlets yet.'), __('Why wait? Make something great.')]
    case 'private_walls':
      return [__("You don't have any private padlets."), __('Create something just for you.')]
    case 'templates':
      return [
        __('No templates yet!'),
        __('To create a template: open a padlet, click the 3-dot button, and select “Mark as template”.'),
      ]
    case 'portfolio':
    case 'contributed':
    case 'network':
    case 'writableNetwork':
    default:
      return ['', '']
  }
}

export function getEmptyFolderCollectionCopy(): [string, string] {
  return [__("You haven't added any padlets to this folder."), __('Ready whenever you are.')]
}

export function getEmptyGroupFolderCollectionCopy(): [string, string] {
  return [__("This group doesn't have any padlets."), __('Add this group as a collaborator on a padlet.')]
}

export function getEmptySearchCollectionCopy(): [string, string] {
  return [__('No results found.'), __('We hope you find what you are looking for.')]
}

const COLLECTIONS_FETCH_LIMITS: Partial<Record<CollectionKey['typeKey'], Record<CollectionKey['indexKey'], number>>> = {
  filter: {
    // only fetch 50 of the most recently present walls
    combined_recents: 50,
  },
}

export function getCollectionFetchLimits({ typeKey, indexKey }: CollectionKey): number | undefined {
  return COLLECTIONS_FETCH_LIMITS[typeKey]?.[indexKey]
}
